import sessionHandling from "services/utils/notificationUtils";

// Labour Welfare Fund APIs (LWF Frontend API Guide, base
// /api/payroll/statutory/lwf/). lwfRequest is shared by every LWF screen
// (Configuration, Statutory › LWF and Reports). The Configuration screens
// keep the lwfConfigData.js form shape ("20" not "20.00", "" for empty,
// months as strings, one set date per deduction month, one components
// list); the mappers here convert to and from the API shape.

const BASE = "payroll/statutory/lwf/";

// 409s the LWF API sends on purpose; any other 409 is the session check.
const LWF_CONFLICT_CODES = ["VERSION_LOCKED", "STALE_VERSION", "RUN_FINALISED", "ALREADY_ADJUSTED", "ALREADY_REVERSED", "NOT_REVERSIBLE"];

export class LwfApiError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message);
    this.name = "LwfApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

const errorText = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(errorText).join(" ");
  if (value && typeof value === "object") return Object.values(value).map(errorText).join(" ");
  return "";
};

// { "bands.0.employee_amount": ["…"] } -> { "bands.0.employee_amount": "…" }
const firstMessages = (error) =>
  error && typeof error === "object" && !Array.isArray(error)
    ? Object.fromEntries(
        Object.entries(error)
          .filter(([key]) => key !== "code")
          .map(([key, messages]) => [key, errorText(Array.isArray(messages) ? messages[0] : messages)]),
      )
    : undefined;

// The guide's optional Idempotency-Key header is not sent (same as ESI:
// the server's CORS settings block requests that carry it). Save buttons
// show a loading state instead, so a double click can't send twice.
// responseType "blob" returns { blob, fileName } for downloads.
export const lwfRequest = async ({ domain, path, method = "GET", query, body, signal, responseType }) => {
  const search = query ? new URLSearchParams(Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")) : null;
  const url = `${domain.replace(/\/$/, "")}/${BASE}${path}${search && String(search) ? `?${search}` : ""}`;
  const response = await fetch(url, {
    method,
    signal,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Token ${localStorage.getItem("token")}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new LwfApiError("Your session has expired. Please sign in again.", { status: 401 });
  }
  if (responseType === "blob" && response.ok) {
    const disposition = response.headers?.get?.("Content-Disposition") || "";
    const fileName = /filename="?([^";]+)"?/i.exec(disposition)?.[1] || "";
    return { blob: await response.blob(), fileName };
  }
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (response.status === 409 && !LWF_CONFLICT_CODES.includes(data?.code)) sessionHandling();
  if (!response.ok) {
    throw new LwfApiError(errorText(data?.error || data?.detail || data) || `Request failed (${response.status}). Please try again.`, {
      status: response.status,
      code: data?.code || data?.error?.code,
      fieldErrors: firstMessages(data?.error),
    });
  }
  return data;
};

const notFoundToNull = (err) => {
  if (err.status === 404) return null;
  throw err;
};

// ---- Value helpers ----

const text = (value) => (value == null ? "" : String(value));
// "20.00" -> "20"; "0.20" -> "0.2"; "20.50" stays "20.5".
export const trimNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const out = String(value);
  return out.includes(".") ? out.replace(/0+$/, "").replace(/\.$/, "") : out;
};
const apiValue = (value) => (value === "" || value == null ? null : value);
const apiInt = (value) => (value === "" || value == null || !Number.isFinite(Number(value)) ? null : Number(value));
const upper = (value) => String(value || "").toUpperCase();
const STATUS_WORD = { ACTIVE: "Active", INACTIVE: "Inactive" };
const statusWord = (value) => STATUS_WORD[upper(value)] || text(value) || "Active";
const VERSION_STATUS_WORD = { DRAFT: "Draft", SAVED: "Active", ACTIVE: "Active", SCHEDULED: "Scheduled", SUPERSEDED: "Superseded" };
// Ids may come as numbers; the screen keeps them as strings.
const idOf = (value) => (value == null ? "" : String(value));
const apiId = (value) => (/^\d+$/.test(String(value)) ? Number(value) : value);

// A list item that may be a string or an object with a name / label.
export const nameOf = (item) =>
  item == null ? "" : typeof item === "object" ? text(item.name ?? item.label ?? item.code ?? item.key ?? item.value) : text(item);

// ---- Set dates ----

// Days in each month for "last day of the month" (February: 28, so the
// date is inside the month every year).
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// API {"12": 31} (monthly {"*": 28}) -> one day per deduction month.
const fromApiSetDates = (setDates, months, frequency) => {
  const dates = setDates && typeof setDates === "object" ? setDates : {};
  if (frequency === "MONTHLY") {
    const day = dates["*"] ?? Object.values(dates)[0];
    return months.map(() => text(day));
  }
  return months.map((month) => text(dates[month] ?? dates[Number(month)]));
};

const apiDay = (day, month) => {
  if (!day) return null;
  if (day === "LAST") return month ? MONTH_DAYS[Number(month) - 1] : 28;
  return Number(day);
};

const toApiSetDates = (form) => {
  if (form.coverage_basis !== "SET_DATE") return {};
  if (form.frequency === "MONTHLY") {
    const day = apiDay(form.set_dates[0]);
    return day ? { "*": day } : {};
  }
  return Object.fromEntries(
    form.deduction_months.map((month, index) => [String(Number(month)), apiDay(form.set_dates[index], month)]).filter(([, day]) => day),
  );
};

// ---- Bands ----

let bandSeq = 5000;
const nextBandId = () => {
  bandSeq += 1;
  return bandSeq;
};

const fromApiBand = (band) => ({
  id: nextBandId(),
  wage_from: trimNumber(band.wage_from) || "0",
  employee_amount: trimNumber(band.employee_amount),
  employer_amount: trimNumber(band.employer_amount),
  employee_pct: trimNumber(band.employee_pct),
  employee_max: trimNumber(band.employee_max),
  employer_multiple: trimNumber(band.employer_multiple),
  employer_max: trimNumber(band.employer_max),
});

const toApiBands = (bands, amountType) =>
  [...bands]
    .sort((a, b) => Number(a.wage_from || 0) - Number(b.wage_from || 0))
    .map((band) =>
      amountType === "PERCENT_CAP"
        ? {
            wage_from: apiValue(band.wage_from),
            employee_pct: apiValue(band.employee_pct),
            employee_max: apiValue(band.employee_max),
            employer_multiple: apiValue(band.employer_multiple),
            employer_max: apiValue(band.employer_max),
          }
        : { wage_from: apiValue(band.wage_from), employee_amount: apiValue(band.employee_amount), employer_amount: apiValue(band.employer_amount) },
    );

// ---- Versions ----

const bySequence = (list) => [...(list || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

const fromApiComponent = (component, kind) => ({
  code: text(component.code),
  name: text(component.name),
  kind,
  description: text(component.description),
  formula: text(component.formula),
  used_for: text(component.used_for),
  status: statusWord(component.status),
  ...(kind === "CONTRIBUTION" || component.fixed ? { fixed: component.fixed ?? true } : {}),
});

export const fromApiLwfVersion = (api) => {
  const frequency = text(api.frequency);
  const months = (api.deduction_months || []).map((month) => String(Number(month)));
  const categories = (api.excluded_categories || []).map((item) =>
    item && typeof item === "object" ? { id: idOf(item.id ?? item.value), label: text(item.label ?? item.name) } : { id: idOf(item), label: "" },
  );
  return {
    id: api.id ?? null,
    name: text(api.name),
    description: text(api.description),
    effective_from: text(api.effective_from),
    effective_to: text(api.effective_to),
    status: api.display_status || VERSION_STATUS_WORD[upper(api.status)] || "Draft",
    applicability: text(api.applicability),
    applicability_basis: text(api.applicability_basis),
    confirmed_by: text(api.confirmed_by),
    confirmed_at: text(api.confirmed_at),
    state: text(api.state),
    registration_no: text(api.registration_no),
    frequency,
    deduction_months: months,
    coverage_basis: text(api.coverage_basis),
    set_dates: fromApiSetDates(api.set_dates, months, frequency),
    payment_due_days: text(api.payment_due_days),
    wage_limit: trimNumber(api.wage_limit),
    excluded_categories: categories.map((item) => item.id),
    // Labels the server sent with the ids, for categories the picker no
    // longer lists (Version History, locked versions).
    category_labels: Object.fromEntries(categories.filter((item) => item.label).map((item) => [item.id, item.label])),
    low_pay_treatment: text(api.low_pay_treatment),
    amount_type: text(api.amount_type) || "FIXED_BAND",
    rounding: text(api.rounding) || "NEAREST",
    bands: (api.bands || []).map(fromApiBand),
    components: [
      ...bySequence(api.calculation_components).map((component) => fromApiComponent(component, "CALCULATION")),
      ...bySequence(api.contribution_components).map((component) => fromApiComponent(component, "CONTRIBUTION")),
    ],
    copied_from: api.copied_from_site ? nameOf(api.copied_from_site) : "",
    source_version_id: api.source_version_id ?? null,
    is_locked: Boolean(api.is_locked),
    used_by: (api.used_by || []).map(nameOf).filter(Boolean),
    row_version: api.row_version ?? null,
    created_by: text(api.saved_by ?? api.created_by),
    created_at: text(api.saved_at ?? api.created_at),
  };
};

// The version body for POST versions/, PUT versions/<id>/, PUT draft/ and
// test-employee (the unsaved version). State comes from the site and is not
// sent; confirmed_by / confirmed_at are set by the server.
export const toApiLwfVersion = (form, companyId) => {
  const kindIndex = { CALCULATION: 0, CONTRIBUTION: 0 };
  return {
    company_id: Number(companyId),
    name: text(form.name).trim(),
    description: text(form.description),
    effective_from: apiValue(form.effective_from),
    applicability: apiValue(form.applicability),
    applicability_basis: text(form.applicability_basis).trim(),
    registration_no: text(form.registration_no).trim(),
    frequency: apiValue(form.frequency),
    deduction_months: form.deduction_months.map(Number),
    coverage_basis: apiValue(form.coverage_basis),
    set_dates: toApiSetDates(form),
    payment_due_days: apiInt(form.payment_due_days),
    wage_limit: apiValue(form.wage_limit),
    excluded_categories: form.excluded_categories.map(apiId),
    low_pay_treatment: apiValue(form.low_pay_treatment),
    amount_type: form.amount_type || "FIXED_BAND",
    rounding: form.rounding || "NEAREST",
    bands: toApiBands(form.bands, form.amount_type),
    components: form.components.map((component) => {
      kindIndex[component.kind] = (kindIndex[component.kind] || 0) + 1;
      return {
        kind: component.kind,
        code: component.code,
        name: component.name,
        description: component.description || "",
        formula: component.formula,
        used_for: component.used_for || "",
        status: upper(component.status),
        sequence: kindIndex[component.kind],
        ...(component.fixed ? { fixed: true } : {}),
      };
    }),
  };
};

// Server field paths -> the screen's field keys (bands.0.employee_amount ->
// bands, set_dates.12 -> set_dates, calculation_components.0.formula ->
// components).
const toFormErrors = (fieldErrors) => {
  if (!fieldErrors) return undefined;
  const out = {};
  Object.entries(fieldErrors).forEach(([key, message]) => {
    const head = key.split(".")[0];
    const field = key.includes("components") ? "components" : head;
    out[field] = out[field] ? `${out[field]} ${message}` : message;
  });
  return out;
};

const withFormErrors = async (promise) => {
  try {
    return await promise;
  } catch (err) {
    if (err instanceof LwfApiError) err.fieldErrors = toFormErrors(err.fieldErrors);
    throw err;
  }
};

const saved = (data) => ({ version: fromApiLwfVersion(data.data), message: data.message });
const asDraft = (data) => ({ version: { ...fromApiLwfVersion(data.data), status: "Draft" }, message: data.message });

// ---- Sites list (LWF1) ----

const SITE_STATUS_TONES = {
  ACTIVE: "green",
  ACTIVE_NEW_VERSION: "amber",
  SCHEDULED: "amber",
  NOT_APPLICABLE: "slate",
  EXEMPT: "slate",
  NOT_SET_UP: "red",
};

const fromApiSiteRow = (row) => {
  const site = row.site || {};
  const status = upper(row.status);
  const setUp = upper(row.action) !== "SET_UP" && !status.startsWith("NOT_SET");
  return {
    id: site.id,
    site: text(site.name),
    code: text(site.code),
    is_active: site.is_active !== false,
    state: text(row.state),
    registration_no: text(row.registration_no),
    frequency_label: text(row.frequency_label),
    deduct_in_label: text(row.deduct_in_label),
    version_label: text(row.current_version?.name),
    effective_from: text(row.effective_from),
    status,
    status_label: text(row.status_label) || (setUp ? "Active" : "Not set up"),
    status_tone: SITE_STATUS_TONES[status] || (setUp ? "green" : "red"),
    set_up: setUp,
    upcoming: status === "ACTIVE_NEW_VERSION",
    action: setUp ? "View" : "Set up",
  };
};

export const fetchLwfSites = async ({ domain, companyId, signal }) => {
  const data = await lwfRequest({ domain, path: "config/sites/", query: { company_id: companyId }, signal });
  if (!data || !Array.isArray(data.sites)) throw new LwfApiError("The server returned an invalid LWF sites list.");
  const rows = data.sites.map(fromApiSiteRow);
  const cards = data.cards || {};
  return {
    rows,
    states: (data.states || []).map(text),
    cards: {
      sites: cards.sites ?? rows.length,
      set_up: cards.set_up ?? rows.filter((row) => row.set_up).length,
      not_set_up: cards.not_set_up ?? rows.filter((row) => !row.set_up).length,
      new_version_coming: cards.new_version_coming ?? rows.filter((row) => row.upcoming).length,
    },
  };
};

// ---- Options ----

const optionList = (list) =>
  Array.isArray(list)
    ? list.map((item) =>
        item && typeof item === "object"
          ? { value: text(item.value ?? item.id ?? item.key ?? item.code), label: text(item.label ?? item.name ?? item.value), help: text(item.help) }
          : { value: text(item), label: text(item) },
      )
    : null;

// Option lists for LWF1a / LWF1b. Employee categories are the company's
// Employment types; the guide does not show the key, so the likely names
// are tried in turn.
export const fetchLwfOptions = async ({ domain, companyId, signal }) => {
  const data = (await lwfRequest({ domain, path: "config/options/", query: { company_id: companyId }, signal })) || {};
  const categories = data.employee_categories ?? data.categories ?? data.employment_types ?? data.excluded_categories;
  return {
    applicability: optionList(data.applicability),
    frequencies: optionList(data.frequencies),
    coverage_basis: optionList(data.coverage_basis),
    low_pay_treatment: optionList(data.low_pay_treatment),
    amount_types: optionList(data.amount_types),
    rounding: optionList(data.rounding),
    categories: optionList(categories) || [],
  };
};

// ---- One site (LWF1a) ----

const versionsOfSite = (detail) => {
  const list = Array.isArray(detail.versions) ? detail.versions : [detail.active_version, detail.scheduled_version];
  const seen = new Set();
  return list.filter((version) => {
    if (!version?.id || upper(version.status) === "DRAFT" || seen.has(version.id)) return false;
    seen.add(version.id);
    return true;
  });
};

// The site, its saved versions (full, oldest first) and its draft, if any.
export const fetchLwfSite = async ({ domain, companyId, siteId, signal }) => {
  const query = { company_id: companyId };
  const [detail, draft] = await Promise.all([
    lwfRequest({ domain, path: `config/sites/${siteId}/`, query, signal }),
    lwfRequest({ domain, path: `config/sites/${siteId}/draft/`, query, signal }).catch(notFoundToNull),
  ]);
  if (!detail || typeof detail !== "object") throw new LwfApiError("The server returned an invalid LWF site.");
  // The version list may carry summaries only: load each one in full.
  const full = await Promise.all(
    versionsOfSite(detail).map((version) =>
      Array.isArray(version.bands) ? version : lwfRequest({ domain, path: `config/versions/${version.id}/`, query, signal }),
    ),
  );
  const versions = full
    .map(fromApiLwfVersion)
    .sort((a, b) => a.effective_from.localeCompare(b.effective_from));
  return {
    site: { id: detail.site?.id ?? siteId, name: text(detail.site?.name), code: text(detail.site?.code), state: text(detail.site?.state) },
    versions: draft ? [...versions, { ...fromApiLwfVersion(draft), status: "Draft" }] : versions,
  };
};

// Set up: an empty Version 1 from the server (not saved; no amounts,
// months or dates filled in).
export const fetchLwfDefaults = async ({ domain, companyId, siteId, signal }) => ({
  ...fromApiLwfVersion(await lwfRequest({ domain, path: `config/sites/${siteId}/defaults/`, query: { company_id: companyId }, signal })),
  id: "new",
  status: "Draft",
});

// Auto-save while a draft is edited: only formats are checked.
export const saveLwfDraft = ({ domain, companyId, siteId, form, signal }) =>
  lwfRequest({ domain, path: `config/sites/${siteId}/draft/`, method: "PUT", body: toApiLwfVersion(form, companyId), signal });

export const discardLwfDraft = async ({ domain, companyId, siteId }) => {
  try {
    await lwfRequest({ domain, path: `config/sites/${siteId}/draft/`, method: "DELETE", body: { company_id: Number(companyId) } });
  } catch (err) {
    if (err.status !== 404) throw err;
  }
};

// Create New Version: the version copied into the site's draft from the
// next free month.
export const createLwfDraftFrom = async ({ domain, companyId, siteId, versionId }) =>
  asDraft(await lwfRequest({ domain, path: `config/sites/${siteId}/draft/from/${versionId}/`, method: "POST", body: { company_id: Number(companyId) } }));

// Copy from site: another site's rules into this site's draft (the
// registration number and basis are not copied).
export const copyLwfDraftFromSite = async ({ domain, companyId, siteId, sourceSiteId }) =>
  asDraft(
    await lwfRequest({
      domain,
      path: `config/sites/${siteId}/draft/copy-from/`,
      method: "POST",
      body: { company_id: Number(companyId), source_site_id: Number(sourceSiteId) },
    }),
  );

// Save: the form becomes a saved version from its month.
export const createLwfVersion = ({ domain, companyId, siteId, form }) =>
  withFormErrors(lwfRequest({ domain, path: `config/sites/${siteId}/versions/`, method: "POST", body: toApiLwfVersion(form, companyId) }).then(saved));

// Save changes to a saved, unlocked version.
export const updateLwfVersion = ({ domain, companyId, form }) =>
  withFormErrors(
    lwfRequest({
      domain,
      path: `config/versions/${form.id}/`,
      method: "PUT",
      body: { ...toApiLwfVersion(form, companyId), row_version: form.row_version },
    }).then(saved),
  );

// ---- Edit Band drawer (LWF1b) ----

// { ok, message, errors, bands: [{ seq, wage_from, wage_to, wage_to_label }] }
export const checkLwfBands = ({ domain, companyId, amountType, bands, signal }) =>
  lwfRequest({
    domain,
    path: "config/bands/check/",
    method: "POST",
    body: { company_id: Number(companyId), amount_type: amountType, bands: toApiBands(bands, amountType) },
    signal,
  });

// What the (unsaved) rule gives one employee in a month; saves nothing.
export const testLwfEmployee = ({ domain, companyId, siteId, employeeId, month, form, signal }) =>
  lwfRequest({
    domain,
    path: "config/test-employee/",
    method: "POST",
    body: {
      company_id: Number(companyId),
      site_id: Number(siteId),
      employee_id: apiId(employeeId),
      month,
      version: toApiLwfVersion(form, companyId),
    },
    signal,
  });

// Employees for "Test on employee" (band and component drawers), from the
// LWF employees list so the ids are the ones test-employee/ expects. This
// site's employees first; the whole company when the site has none yet.
const TEST_EMPLOYEE_PAGE = 200;
const testEmployeeRow = (item) => ({
  id: String(item.employee_id ?? item.id ?? ""),
  name: text(item.name),
  code: text(item.employee_code),
  site: text(item.site?.name),
});
export const fetchLwfTestEmployees = async ({ domain, companyId, siteId, signal }) => {
  const load = async (site) => {
    const data = await lwfRequest({
      domain,
      path: "employees/",
      query: { company_id: companyId, site_id: site, status: "ALL", page: 1, page_size: TEST_EMPLOYEE_PAGE },
      signal,
    });
    const results = Array.isArray(data) ? data : data?.results || [];
    return results.map(testEmployeeRow).filter((item) => item.id && item.name);
  };
  const atSite = siteId ? await load(siteId) : [];
  return atSite.length ? atSite : load(undefined);
};

// ---- Component drawer (LWF1a) ----

// { valid, message, codes } — "✓ Formula is valid" or the error.
export const checkLwfFormulaOnServer = ({ domain, companyId, code, formula, signal }) =>
  lwfRequest({
    domain,
    path: "config/formula/check/",
    method: "POST",
    body: { company_id: Number(companyId), code, formula },
    signal,
  });
